import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as XLSX from "xlsx";
import HelperClass from "./../../../config/helperClass";
import {
  getClientInvoices,
  getStripeClientConfig,
  removeStripeSavedMethod,
} from "./../../../config/client_api_calls";
import { addSettings } from "../../../actions";
import MsaWarning from "../ExtraComponents/MsaWarning";
import SettingsError from "../ExtraComponents/SettingsError";
import ViewInvoiceModal from "../../../components/Invoices/ViewInvoiceModal";
import StripePayModal from "../../../components/Invoices/StripePayModal";
import NotificationToast from "../../../components/Notification/NotificationToast";
import "./styles.css";

const auth = new HelperClass();

// ── Helpers ───────────────────────────────────────────────────────────────────

function isOverdue(row) {
  if (!row.due_date) return false;
  if (row.payment_status === "Paid") return false;
  return new Date(row.due_date) < new Date();
}

function fmtCompact(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0";
  if (n >= 1000) return "$" + (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return "$" + n.toFixed(0);
}

function fmtDate(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()}`;
  } catch {
    return val;
  }
}

function fmtCurrency(val, decimals) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0.00";
  const fixed = n.toFixed(decimals || 2);
  const [intPart, decPart] = fixed.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return "$" + (decPart !== undefined ? withCommas + "." + decPart : withCommas);
}

function getInvoiceBalance(invoice) {
  if (invoice != null && invoice.balance_due != null) {
    const stored = Number(invoice.balance_due);
    if (Number.isFinite(stored)) return Math.max(0, stored);
  }
  return Math.max(
    0,
    (Number(invoice && invoice.grand_total) || 0) -
      (Number(invoice && invoice.credit_applied) || 0) -
      (Number(invoice && invoice.amount_paid) || 0)
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status, row }) {
  const overdue = isOverdue(row);
  const viaStripe = row && row.payment_method === "stripe";
  if (status === "Paid") {
    return (
      <span className="inv-status-badge stripe-paid" style={{ background: "rgba(26,122,74,0.1)", color: "#1A7A4A" }}>
        {viaStripe ? "Paid via Stripe" : "Paid"}
      </span>
    );
  }
  // A bank debit that Stripe has accepted but not yet cleared.
  if (row && row.stripe_payment_status === "processing") {
    return <span className="inv-status-badge stripe-processing">Processing</span>;
  }
  if (row && row.stripe_payment_status === "failed" && status !== "Partial") {
    return <span className="inv-status-badge stripe-failed" title="The last Stripe payment attempt failed">Payment failed</span>;
  }
  if (status === "Partial") {
    return (
      <span className="inv-status-badge" style={{ background: "rgba(79,70,229,0.1)", color: "#4F46E5" }}>
        Partial
      </span>
    );
  }
  if (overdue) {
    return (
      <span className="inv-status-badge" style={{ background: "rgba(220,38,38,0.12)", color: "#DC2626" }}>
        Unpaid
      </span>
    );
  }
  return (
    <span className="inv-status-badge" style={{ background: "rgba(245,158,11,0.1)", color: "#D97706" }}>
      Unpaid
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

class Invoices extends Component {
  constructor(props) {
    super(props);
    console.log("[Invoices] constructor → props:", props);
    this._decimals = 2; // resolved in componentDidMount from quotationSettings
    this._searchTimer = null;

    this.state = {
      // ── toast ──
      toastMsg: "",
      toastType: "success",

      // ── list ──
      rows: [],
      total: 0,
      summary: { total_invoiced: 0, total_paid: 0, total_outstanding: 0, invoice_count: 0 },
      page: 1,
      limit: 10,
      search: "",
      statusFilter: "",
      orderFilter: "",
      dateFrom: "",
      dateTo: "",
      loading: false,
      errorMessage: "",
      filtersApplied: false,

      // ── view ──
      selectedInvoice: null,
      showViewModal: false,

      // ── stripe ──
      stripeConfig: null,
      payInvoice: null,
      showPayModal: false,
      payReturn: null,
      pendingPayInvoiceNo: "",
    };
  }

  componentDidMount() {
    console.log("[Invoices] componentDidMount");
    console.log("[Invoices] location:", this.props.location);
    console.log("[Invoices] clientAccount:", auth.getClientAccount());
    console.log("[Invoices] clientToken:", auth.getClientToken());
    const qs = this.props.quotationSettings;
    if (qs && qs[0]) {
      const dp = qs[0].billing_decimal_point || qs[0].decimal_point;
      if (dp && dp !== "") this._decimals = Number(dp);
    }
    console.log("[Invoices] _decimals:", this._decimals);
    const params = new URLSearchParams(
      this.props.location && this.props.location.search ? this.props.location.search : ""
    );
    const orderno = params.get("orderno");

    // Stripe: a Checkout return (?invoice=INV&stripe=success|cancel&session_id=…)
    // or a deep link to pay (?pay=INV or #pay/INV, as the Sales Orders page sends).
    const stripeRet = params.get("stripe");
    const retInvoice = params.get("invoice");
    const hashPay = (window.location.hash || "").match(/^#pay\/([\w-]+)$/);
    const payNo = params.get("pay") || (hashPay ? hashPay[1] : "");
    if (stripeRet && retInvoice) {
      this.setState({
        pendingPayInvoiceNo: retInvoice,
        payReturn: { kind: stripeRet === "success" ? "success" : "cancel", session_id: params.get("session_id") || "" },
      });
    } else if (payNo) {
      this.setState({ pendingPayInvoiceNo: payNo });
    }
    if (stripeRet || payNo || hashPay) {
      ["stripe", "session_id", "invoice", "pay"].forEach((k) => params.delete(k));
      const clean = params.toString();
      this.props.history && this.props.history.replace({ search: clean ? `?${clean}` : "", hash: "" });
    }

    this.loadStripeConfig();
    if (orderno) {
      console.log("[Invoices] orderno from URL:", orderno);
      this.setState({ orderFilter: orderno }, this.fetchInvoices);
    } else {
      this.fetchInvoices();
    }
  }

  // ── Stripe ────────────────────────────────────────────────────────────────

  loadStripeConfig = async () => {
    try {
      const res = await getStripeClientConfig(auth.getClientAccount(), auth.getClientToken());
      const payload = res && res.data;
      if (payload && payload.status === 200 && payload.data) {
        this.setState({ stripeConfig: payload.data }, this.openPendingPay);
        return;
      }
      // Not an error for the client — the page simply reads as it did before
      // Stripe existed — but silence here is the first thing to check when the
      // Pay button is expected and missing, so say why.
      console.warn(
        "[Invoices] Stripe unavailable, Pay buttons stay hidden. " +
          "The backend did not return a client config (is stripe/client-config deployed?). Response:",
        payload
      );
    } catch (e) {
      console.warn("[Invoices] Stripe config request failed, Pay buttons stay hidden:", e);
    }
  };

  isPayable = () => {
    const c = this.state.stripeConfig;
    return !!(c && c.payable);
  };

  /** Deep links and Checkout returns open the sheet once both rows and config are in. */
  openPendingPay = () => {
    const no = this.state.pendingPayInvoiceNo;
    if (!no || this.state.loading) return;
    const row = this.state.rows.find((r) => r.invoice_no === no);
    if (!row) return;
    const ret = this.state.payReturn;
    if (ret) {
      this.setState({ payInvoice: row, showPayModal: true, pendingPayInvoiceNo: "" });
    } else if (getInvoiceBalance(row) > 0 && this.isPayable()) {
      this.setState({ payInvoice: row, showPayModal: true, pendingPayInvoiceNo: "" });
    } else {
      this.setState({ pendingPayInvoiceNo: "" }, () => this.openDetail(row));
    }
  };

  openPay = (row) => {
    if (!row || !this.isPayable() || getInvoiceBalance(row) <= 0) return;
    this.setState({ showViewModal: false, selectedInvoice: null, payInvoice: row, showPayModal: true, payReturn: null });
  };

  payFirstOwing = () => {
    const row = this.state.rows.find((r) => getInvoiceBalance(r) > 0);
    if (row) this.openPay(row);
  };

  closePay = () => {
    this.setState({ showPayModal: false, payInvoice: null, payReturn: null });
  };

  onPaid = (statusData) => {
    // The ledger has (or has not) caught up; refresh the list and the config
    // (a card may have been saved) without closing the receipt.
    this.fetchInvoices();
    this.loadStripeConfig();
    if (statusData && statusData.payment_status === "Paid") {
      this.setState({ toastMsg: `${statusData.invoice_no} is settled in full.`, toastType: "success" });
    }
  };

  removeSavedCard = async () => {
    try {
      const res = await removeStripeSavedMethod(auth.getClientAccount(), auth.getClientToken());
      const payload = res && res.data;
      this.setState({
        toastMsg: (payload && payload.message) || "Card removed.",
        toastType: payload && payload.status === 200 ? "success" : "error",
      });
      this.loadStripeConfig();
    } catch (e) {
      this.setState({ toastMsg: "Could not remove the card.", toastType: "error" });
    }
  };

  renderPayLead() {
    if (!this.isPayable()) return null;
    const { summary, rows } = this.state;
    const d = this._decimals;
    const outstanding = Number(summary.total_outstanding) || 0;
    const owing = rows.filter((r) => getInvoiceBalance(r) > 0).length;
    const cfg = this.state.stripeConfig || {};
    const sm = cfg.saved_method;
    const canBank = cfg.methods && (cfg.methods.ach || cfg.methods.sepa || cfg.methods.bacs);
    return (
      <div className="inv-lead">
        <div className="inv-lead-top">
          <div>
            <div className="inv-lead-h">{outstanding > 0 ? `${fmtCurrency(outstanding, d)} outstanding` : "Nothing outstanding"}</div>
            <div className="inv-lead-s">
              {outstanding > 0
                ? `${owing || ""}${owing ? ` invoice${owing === 1 ? "" : "s"} to settle. ` : ""}Pay by card${canBank ? " or bank debit" : ""} here, or by transfer using the details on the invoice.`
                : "Every invoice is settled. Thank you."}
            </div>
          </div>
          {outstanding > 0 && owing > 0 && (
            <button type="button" className="inv-lead-pay" onClick={this.payFirstOwing}>Pay now</button>
          )}
        </div>
        {sm && cfg.methods && cfg.methods.card && (
          <div className="inv-pm-line">
            <span className="inv-pm-card">
              <span className="inv-pm-brand">{sm.brand || "Card"}</span> •••• {sm.last4}
            </span>
            <span>on file, expires {sm.exp_month ? String(sm.exp_month).padStart(2, "0") : "--"} / {sm.exp_year ? String(sm.exp_year).slice(-2) : "--"}</span>
            <button type="button" className="inv-pm-remove" onClick={this.removeSavedCard}>Remove</button>
          </div>
        )}
        {cfg.mode === "test" && <div className="inv-stripe-note">Stripe is in test mode — no money moves.</div>}
      </div>
    );
  }

  // ── Data fetching ─────────────────────────────────────────────────────────

  fetchInvoices = async () => {
    const { page, limit, search, statusFilter, orderFilter, dateFrom, dateTo } = this.state;
    console.log("[Invoices] fetchInvoices → params:", { page, limit, search, statusFilter, orderFilter, dateFrom, dateTo });
    this.setState({ loading: true, errorMessage: "" });
    try {
      const response = await getClientInvoices(
        auth.getClientAccount(), auth.getClientToken(),
        page, limit, search, statusFilter, dateFrom, dateTo, orderFilter
      );
      console.log("[Invoices] fetchInvoices → raw response:", response);
      const payload = response && response.data;
      console.log("[Invoices] fetchInvoices → payload:", payload);
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        console.warn("[Invoices] auth error → logging out:", payload);
        auth.clientLogout();
        return;
      }
      if (payload && payload.status === 200 && payload.data) {
        console.log("[Invoices] fetchInvoices → success, rows:", payload.data.rows, "total:", payload.data.total);
        this.setState({
          rows: payload.data.rows || [],
          total: Number(payload.data.total) || 0,
          summary: payload.data.summary || this.state.summary,
          loading: false,
        }, this.openPendingPay);
      } else {
        const msg = (payload && payload.message) || "Failed to load invoices.";
        console.error("[Invoices] fetchInvoices → unexpected payload:", payload, "msg:", msg);
        this.setState({ loading: false, errorMessage: msg, toastMsg: msg, toastType: "error" });
      }
    } catch (err) {
      console.error("[Invoices] fetchInvoices → caught error:", err);
      this.setState({
        loading: false,
        errorMessage: "Failed to load invoices.",
        toastMsg: "Failed to load invoices.",
        toastType: "error",
      });
    }
  };

  openDetail = (row) => {
    console.log("[Invoices] openDetail → row:", row);
    console.log("[Invoices] invoiceNo passed to modal:", row && row.invoice_no);
    this.setState({ showViewModal: true, selectedInvoice: row });
  };

  closeViewModal = () => {
    this.setState({ showViewModal: false, selectedInvoice: null });
  };

  // ── Pagination / filters ──────────────────────────────────────────────────

  onPageChange   = (page)  => this.setState({ page },            this.fetchInvoices);
  onLimitChange  = (limit) => this.setState({ limit, page: 1 }, this.fetchInvoices);
  onSearchChange = (e) => {
    const search = e.target.value;
    this.setState({ search, page: 1, filtersApplied: true });
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(this.fetchInvoices, 400);
  };
  onStatusFilter = (e) => {
    const statusFilter = e.target.value;
    const { search, orderFilter, dateFrom, dateTo } = this.state;
    const hasOtherFilters = search || orderFilter || dateFrom || dateTo;
    this.setState({ statusFilter, page: 1, filtersApplied: statusFilter !== "" || hasOtherFilters }, this.fetchInvoices);
  };
  onOrderFilter  = (e) => this.setState({ orderFilter: e.target.value,  page: 1, filtersApplied: true }, this.fetchInvoices);
  onDateFrom     = (e) => this.setState({ dateFrom: e.target.value,     page: 1, filtersApplied: true }, this.fetchInvoices);
  onDateTo       = (e) => this.setState({ dateTo: e.target.value,       page: 1, filtersApplied: true }, this.fetchInvoices);

  clearOrderFilter = () => {
    const params = new URLSearchParams(
      this.props.location && this.props.location.search ? this.props.location.search : ""
    );
    if (params.has("orderno")) {
      params.delete("orderno");
      const newSearch = params.toString();
      this.props.history && this.props.history.replace({ search: newSearch ? `?${newSearch}` : "" });
    }
    this.setState({ orderFilter: "" }, this.fetchInvoices);
  };


  exportToExcel = () => {
    const { rows } = this.state;
    if (!rows || rows.length === 0) return;

    const data = rows.map((r) => ({
      "Invoice #":      r.invoice_no || "",
      "Order #":        r.orderno || "",
      "Billing Period": r.billing_period || "",
      "Invoice Type":   r.invoice_type === "partial" ? "Partial Invoice" : r.invoice_type === "follow_up" ? "Follow-up Invoice" : "Standard Invoice",
      "Issue Date":     r.invoice_date ? r.invoice_date.slice(0, 10) : "",
      "Due Date":       r.due_date     ? r.due_date.slice(0, 10)     : "",
      "Currency":       r.currency || "USD",
      "Grand Total":    Number(r.grand_total  || 0),
      "Amount Paid":    Number(r.amount_paid  || 0),
      "Balance Due":    Number(r.balance_due  || 0),
      "Payment Status": r.payment_status || "",
      "Payment Terms":  r.payment_terms  || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 18 }, { wch: 14 }, { wch: 24 }, { wch: 18 },
      { wch: 12 }, { wch: 12 }, { wch: 8  }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, `Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Render: KPI cards ─────────────────────────────────────────────────────

  renderKpiCards() {
    const { summary, rows } = this.state;
    const overdueCount  = rows.filter(isOverdue).length;
    const unpaidCount   = rows.filter(r => r.payment_status !== "Paid").length;
    const paidCount     = rows.filter(r => r.payment_status === "Paid").length;
    const totalCollected = rows.reduce((sum, r) => sum + (Number(r.amount_paid) || 0), 0);

    const cards = [
      {
        label: "TOTAL INVOICES", value: String(Number(summary.invoice_count) || 0), sub: "All time",
        iconBg: "#ede9fb", iconColor: "#7c3aed", icon: "ni ni-file-docs",
      },
      {
        label: "TOTAL OUTSTANDING", value: fmtCompact(summary.total_outstanding),
        sub: `${unpaidCount} unpaid`, subExtra: overdueCount > 0 ? `· ${overdueCount} overdue` : null,
        iconBg: "#fff0e6", iconColor: "#ea580c", icon: "ni ni-clock",
      },
      {
        label: "TOTAL PAID", value: fmtCompact(totalCollected),
        sub: `${paidCount} paid invoice${paidCount !== 1 ? "s" : ""}`,
        iconBg: "#e6f9f0", iconColor: "#16a34a", icon: "ni ni-check-circle",
      },
    ];

    return (
      <div className="inv-kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {cards.map((c, i) => (
          <div className="inv-kpi-card" key={i}>
            <div className="inv-kpi-left">
              <div className="inv-kpi-label">{c.label}</div>
              <div className="inv-kpi-value">{c.value}</div>
              <div className="inv-kpi-sub">
                {c.sub}
                {c.subExtra && <span className="inv-kpi-overdue-chip">{c.subExtra}</span>}
              </div>
            </div>
            <div className="inv-kpi-icon" style={{ background: c.iconBg, color: c.iconColor }}>
              <em className={`icon ${c.icon}`}></em>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Render: page header ───────────────────────────────────────────────────

  renderPageHeader() {
    return (
      <div className="nk-block-head nk-block-head-sm">
        <div className="nk-block-between flex-wrap g-3">
          <div className="nk-block-head-content">
            <h3 className="nk-block-title page-title">Invoices</h3>
            <div className="nk-block-des text-soft"><p>Your invoice history and payment records</p></div>
          </div>
          <div className="nk-block-head-content">
            <div className="toggle-wrap nk-block-tools-toggle">
              <ul className="nk-block-tools g-3">
                <li className="nk-block-tools-opt">
                  <button
                    type="button"
                    className="btn btn-info"
                    style={{ color: "#fff" }}
                    onClick={this.exportToExcel}
                    disabled={!this.state.rows.length}
                  >
                    <em className="icon ni ni-download"></em>&nbsp;Export
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: filter bar ────────────────────────────────────────────────────

  renderFilters() {
    const { search, statusFilter, orderFilter } = this.state;

    return (
      <>
        {orderFilter && (
          <div className="inv-order-filter-banner">
            <em className="icon ni ni-filter-alt" style={{ marginRight: 6 }}></em>
            Filtering by Order <strong>{orderFilter}</strong>.{" "}
            <span className="inv-order-filter-clear" onClick={this.clearOrderFilter}>
              Show all →
            </span>
          </div>
        )}
        <div className="row g-2 align-items-center">
          <div className="col-12 col-sm col-md">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search invoices…"
              value={search}
              onChange={this.onSearchChange}
            />
          </div>
          <div className="col-12 col-sm-auto" style={{ minWidth: 150 }}>
            <select className="form-control form-control-sm" value={statusFilter} onChange={this.onStatusFilter}>
              <option value="">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Unpaid</option>
              <option value="Partial">Partial</option>
            </select>
          </div>
        </div>
      </>
    );
  }

  // ── Render: pagination ────────────────────────────────────────────────────

  renderPagination() {
    const { page, limit, total, loading } = this.state;
    if (total <= 0) return null;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const buildPages = () => {
      const from = Math.max(1, Math.min(page - 2, totalPages - 4));
      const to = Math.min(totalPages, from + 4);
      const pages = [];
      for (let p = from; p <= to; p++) pages.push(p);
      return pages;
    };

    return (
      <div className="card-inner">
        <div className="nk-block-between-md g-3">
          <div className="g">
            <span style={{ color: "#666", fontSize: 13 }}>
              Showing {start}–{end} of {total}
            </span>
          </div>
          <div className="g">
            <ul className="pagination justify-content-center justify-content-md-end">
              <li className={"page-item " + (page <= 1 || loading ? "disabled" : "")}>
                <button type="button" className="page-link" onClick={() => this.onPageChange(page - 1)} disabled={page === 1 || loading}>
                  Prev
                </button>
              </li>
              {buildPages().map((p) => (
                <li key={p} className={"page-item " + (p === page ? "active" : "")}>
                  <button type="button" className="page-link" onClick={() => this.onPageChange(p)} disabled={loading}>
                    {p}
                  </button>
                </li>
              ))}
              <li className={"page-item " + (page >= totalPages || loading ? "disabled" : "")}>
                <button type="button" className="page-link" onClick={() => this.onPageChange(page + 1)} disabled={page === totalPages || loading}>
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: listing table ─────────────────────────────────────────────────

  renderList() {
    const { rows, total, loading } = this.state;
    const d = this._decimals;

    const columns = [
      {
        name: "invoice_no", label: "INVOICE #",
        render: (row) => (
          <span className="inv-table-link" onClick={() => this.openDetail(row)}>
            {row.invoice_no}
          </span>
        ),
      },
      {
        name: "orderno", label: "ORDER #",
        render: (row) => <span className="inv-table-mono">{row.orderno || "—"}</span>,
      },
      {
        name: "billing_period", label: "BILLING PERIOD",
        render: (row) => (
          <span className="inv-table-muted" style={{ whiteSpace: "nowrap" }}>
            {row.billing_period || "—"}
          </span>
        ),
      },
      {
        name: "amount", label: "TOTAL AMOUNT",
        render: (row) => <span className="inv-table-amount">{fmtCurrency(row.grand_total, d)}</span>,
      },
      {
        name: "credit_applied", label: "CREDIT APPLIED",
        render: (row) => {
          const credit = Number(row.credit_applied) || 0;
          return (
            <span style={{ color: credit > 0 ? "#1A7A4A" : "#9CA3AF" }}>
              {credit > 0 ? fmtCurrency(credit, d) : "—"}
            </span>
          );
        },
      },
      {
        name: "paid", label: "Paid Amount",
        render: (row) => {
          const paid = Number(row.amount_paid) || 0;
          return <span style={{ color: paid > 0 ? "#1A7A4A" : "#9CA3AF" }}>{fmtCurrency(paid, d)}</span>;
        },
      },
      {
        name: "balance", label: "BALANCE",
        render: (row) => {
          const balance = getInvoiceBalance(row);
          const settled = balance <= 0;
          return settled
            ? <span style={{ color: "#1A7A4A", fontWeight: 600 }}>✓ Settled</span>
            : <span style={{ color: "#DC2626", fontWeight: 600 }}>{fmtCurrency(balance, d)}</span>;
        },
      },
      {
        name: "status", label: "STATUS",
        render: (row) => <StatusBadge status={row.payment_status} row={row} />,
      },
      {
        name: "actions", label: "ACTIONS",
        render: (row) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {this.isPayable() && getInvoiceBalance(row) > 0 && (
              <button
                type="button"
                className="inv-inline-pay-btn"
                onClick={(e) => { e.stopPropagation(); this.openPay(row); }}
                title="Pay with Stripe"
              >
                Pay
              </button>
            )}
            <span
              className="inv-inline-view-btn"
              onClick={(e) => { e.stopPropagation(); this.openDetail(row); }}
              title="View Invoice"
            >
              <em className="icon ni ni-eye"></em>
            </span>
          </span>
        ),
      },
    ];

    const { search, statusFilter, orderFilter, dateFrom, dateTo, filtersApplied } = this.state;
    const showEmptyState = total === 0 && !loading && !filtersApplied;

    // Fixed column widths, as the SalesCRM grid does, so table-layout: fixed holds.
    const colWidths = [130, 110, 150, 120, 120, 120, 120, 110, 100];
    const rightAlign = (col) => (col.name === "actions" ? { textAlign: "right" } : undefined);
    // Actions stays pinned to the right edge while the grid scrolls sideways.
    const cellClass = (col) => "tb-tnx-info" + (col.name === "actions" ? " inv-col-sticky" : "");

    return (
      <div className="card card-bordered">
        <div className="card-inner-group">
          <div className="card-inner">{this.renderFilters()}</div>
          <div className="card-inner p-0" style={{ borderTop: "1px solid #e5e9f2", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table className="table table-tranx inv-list-table" style={{ tableLayout: "fixed", marginBottom: 0 }}>
              <colgroup>
                {colWidths.map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <thead className="tb-tnx-head">
                <tr className="tb-tnx-item">
                  {columns.map((col) => (
                    <th key={col.name} className={cellClass(col)} style={rightAlign(col)}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {showEmptyState ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px" }}>
                      <div className="inv-empty-state" style={{ margin: 0 }}>
                        <div className="inv-empty-icon">🧾</div>
                        <div className="inv-empty-title">No invoices yet</div>
                        <div className="inv-empty-sub">Your invoices will appear here once they are issued.</div>
                      </div>
                    </td>
                  </tr>
                ) : loading ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px", color: "#6B6762" }}>
                      <span className="spinner-border spinner-border-sm" />
                      <span style={{ marginLeft: "8px" }}>Loading…</span>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px", color: "#9A9690" }}>
                      No invoices match your filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className="tb-tnx-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => this.openDetail(row)}
                    >
                      {columns.map((col) => (
                        <td key={col.name} className={cellClass(col)} style={rightAlign(col)}>
                          {typeof col.render === "function"
                            ? col.render(row)
                            : row[col.name] != null ? String(row[col.name]) : ""}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && rows.length > 0 && this.renderPagination()}
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    console.log("[Invoices] render → state:", this.state, "defaultSettings:", this.props.defaultSettings, "is_msa_signed:", this.props.is_msa_signed);
    return (
      <div className="nk-content">
        <div className="container-fluid">
          {this.props.defaultSettings === "missing" ? <SettingsError /> : null}
          {this.props.is_msa_signed === "No" ? <MsaWarning /> : null}
          <div className="nk-content-inner">
            {/* rem-invoice-wrap stays on an ancestor: it scopes the .inv-* rules and CSS variables */}
            <div className="nk-content-body rem-invoice-wrap">
              {this.renderPageHeader()}
              {this.renderKpiCards()}
              {this.renderPayLead()}
              {this.renderList()}
            </div>
          </div>
        </div>

        <ViewInvoiceModal
          show={this.state.showViewModal}
          invoiceNo={this.state.selectedInvoice && this.state.selectedInvoice.invoice_no}
          onClose={this.closeViewModal}
          payable={this.isPayable()}
          stripeMethods={this.state.stripeConfig && this.state.stripeConfig.methods}
          onPay={(inv) => this.openPay(this.state.selectedInvoice || inv)}
        />
        <StripePayModal
          show={this.state.showPayModal}
          invoice={this.state.payInvoice}
          config={this.state.stripeConfig}
          decimals={this._decimals}
          initialReturn={this.state.payReturn}
          onClose={this.closePay}
          onPaid={this.onPaid}
        />
        <NotificationToast
          message={this.state.toastMsg}
          type={this.state.toastType}
          onDismiss={() => this.setState({ toastMsg: "" })}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  defaultSettings: state.settings,
  is_msa_signed: state.is_msa_signed,
  quotationSettings: state.quotationSettings,
});
const mapDispatchToProps = (dispatch) => ({
  addSettings: (data) => dispatch(addSettings(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Invoices));
